import MapIcon from '@mui/icons-material/Map';
import {
  Avatar,
  Box,
  Card,
  Chip,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { HOST_API_URL } from '../../../../config-global';
import { getStateAlerts } from '../../../../api/orders';
import { editTicketData, getActiveTickets } from '../../../../redux/slices/tickets';
import { useDispatch, useSelector } from '../../../../redux/store';
import { CustomAvatar } from '../../../../reusable-components/custom-avatar';
import Iconify from '../../../../reusable-components/iconify';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import ManagerChips from '../../../../reusable-components/datagrid/ManagerChips';
import WeatherInfo from '../../../../reusable-components/weather-info/WeatherInfo';
import NewUser from './NewUser';
import TicketRecipients from './TicketRecipients';
import FacilityContacts from './FacilityContacts';
import TicketSharedContent from './TicketSharedContent';
import { getFullFacilityList } from './newTicket/TicketHelper';

export default function TicketInfo() {
  const { id = '' } = useParams();
  const hasFacilityAccess = useScopeCheck(['Facility-Manager'], true);

  const { activeTickets } = useSelector((state) => state.tickets.data) || {};
  const { subtickets, mainTicket, ticketUsers } = useSelector((state) => state.tickets.data.ticketInfoById[id]) || {};
  const ticketManagers = activeTickets.find((t) => t.id === Number(id))?.facilityManagerAssignments;
  const { facilities } = useSelector((state) => state.tickets.data);
  const dispatch = useDispatch();
  const [editFacility, setEditFacility] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [weatherInfo, setWeatherInfo] = useState(null);
  const [stateAlerts, setStateAlerts] = useState([]);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [hasFetchedWeather, setHasFetchedWeather] = useState(false);
  const {
    data: { users: allUsers },
  } = useSelector((state) => state.tickets);
  //--menu
  const [managerParams, setManagerParams] = useState(null);

  //-- end menu
  useEffect(() => {
    if (!activeTickets?.length) {
      dispatch(getActiveTickets());
    }
  }, []);

  useEffect(() => {
    const recipients = [];

    ticketUsers?.forEach((user) => {
      if (!user.userName) {
        const userObj = allUsers.find((u) => u.id === user.userEmail);
        recipients.push({ id: user.userEmail, value: userObj?.value || null });
      } else {
        recipients.push({ id: user.userEmail, value: user.userName });
      }
    });

    setParticipants(recipients);
  }, [allUsers, ticketUsers]);

  const handleDelete = (newValue) => {
    editTicket({
      Id: id,
      value: newValue.filter((user) => user.id).map((user) => user.id),
      updateType: 'Users',
    });
    setParticipants(newValue);
  };

  const [facility, setFacility] = useState(facilities.find((facility) => facility.id === mainTicket?.facilityId));
  const facilityDropdownOptions = [...getFullFacilityList(facilities)]
    .sort((a, b) => ((a.shortName || a.facilityName) < (b.shortName || b.facilityName) ? -1 : 1))
    .map((f) => (
      <MenuItem key={f.id} value={f.id}>
        {f.shortName || f.facilityName}
      </MenuItem>
    ));
  const { enqueueSnackbar } = useSnackbar();

  const updateManagerParamsRows = (updatedRow) => {
    setManagerParams((prev) => ({
      ...prev,
      row: {
        ...prev.row,
        ...updatedRow[0],
      },
    }));
  };

  useEffect(() => {
    setManagerParams({
      row: {
        facilityManagerAssignments: ticketManagers,
        facilityId: facility?.id,
        id,
      },
      api: { updateRows: updateManagerParamsRows },
    });
  }, [ticketManagers, facility, id]);

  useEffect(() => {
    setFacility(facilities.find((facility) => facility.id === mainTicket?.facilityId));
  }, [mainTicket, facilities]);

  const editTicket = async ({ Id, value, updateType }) => {
    const response = await dispatch(editTicketData({ Id, value, updateType }));
    if (response) {
      enqueueSnackbar('Ticket updated successfully', { variant: 'success' });
    } else {
      enqueueSnackbar('Ticket failed to update', { variant: 'error' });
    }
    return response;
  };

  const handleChange = async (event) => {
    setEditFacility(false);
    const edited = await editTicket({ Id: id, value: event.target.value, updateType: 'FacilityId' });
    if (edited) setFacility(facilities.find((f) => f.id === event.target.value));
  };

  const handleCancel = () => {
    setEditFacility(false);
  };

  const downloadFacilityMap = async () => {
    window.open(`${HOST_API_URL}Api/FacilityMapFile?id=${facility.id}`, '_blank');
  };

  const getZipCode = (address) => {
    const zipCodeRegex = /\b\d{5}(?:-\d{4})?\b/;
    const match = address?.match(zipCodeRegex);
    return match ? match[0] : null;
  };
  
useEffect(() => {
  if (!getZipCode(facility?.address)) return;
  const fetchWeather = async () => {
    try {
      setLoadingWeather(true);
      const res = await getStateAlerts(getZipCode(facility?.address));
      setWeatherInfo(res?.weather ?? null);
      setStateAlerts(res?.headlines ?? []);
    } catch (e) {
      setWeatherInfo(null);
      setStateAlerts([]);
    } finally {
      setLoadingWeather(false);
      setHasFetchedWeather(true);
    }
  };

  fetchWeather();
}, [facility?.address]);

  return (
    <>
      <Card sx={{ height: '100%', mb: 2, p: 3 }}>
        <Grid container direction="column" justifyContent="center" alignItems="flex-start" sx={{ width: '100%' }}>
          <Grid item sx={{ width: '100%' }}>
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ width: '100%' }}
            >
              <Grid item sx={{ width: '100%' }}>
                <Grid
                  container
                  direction="column"
                  justifyContent="center"
                  alignItems="flex-start"
                  sx={{ width: '100%' }}
                >
                  {mainTicket?.facility && (
                    <Grid item sx={{ pb: 2 }}>
                      {editFacility ? (
                        <>
                          <Select
                            value={facility?.id}
                            onChange={handleChange}
                            displayEmpty
                            sx={{
                              textAlign: 'left',
                              minWidth: 150,
                              borderRadius: '5px',
                              maxHeight: '30px',
                              border: `solid 1px info.main`,
                              marginLeft: '-15px',
                              fontSize: '15px',
                              color: (theme) => theme.palette.info.main,
                              '& .MuiSelect-icon': {
                                color: (theme) => theme.palette.info.main,
                              },
                            }}
                          >
                            {facilityDropdownOptions}
                          </Select>
                          <IconButton onClick={() => handleCancel()} sx={{ ml: 0 }} color="primary">
                            <Iconify icon="material-symbols:cancel" width={15} height={5} sx={{ mr: 0.5 }} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Grid container direction="row" justifyContent="flex-start">
                            <Grid item sx={{ mr: 3 }}>
                              {facility?.logo?.length > 0 ? (
                                <Box
                                  component="img"
                                  src={facility?.logo}
                                  sx={{
                                    color: 'white.main',
                                    height: '35px',
                                    objectFit: 'contain',
                                    borderRadius: `2px`,
                                  }}
                                />
                              ) : (
                                <Avatar sx={{ p: 1, bgcolor: (theme) => theme.palette.info.main }}>
                                  <Iconify icon="mdi:building" width={30} height={30} />
                                </Avatar>
                              )}
                            </Grid>
                            <Grid item>
                              <Typography variant="h5" color="info.main">
                                {facility?.shortName || facility?.facilityName}
                                {!mainTicket?.locked && (
                                  <IconButton onClick={() => setEditFacility(true)} sx={{ ml: 0 }} color="primary">
                                    <Iconify icon="material-symbols:edit" width={15} height={5} sx={{ mr: 0.5 }} />
                                  </IconButton>
                                )}
                              </Typography>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  )}
                  {facility?.company && (
                    <Grid item>
                      <Tooltip title="Group">
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                          <Typography variant="subtitle2" display="inline">
                            <Iconify icon="mdi:company" width={16} height={16} color="secondary" sx={{ mr: 0.5 }} />
                          </Typography>
                          <Typography variant="subtitle2" display="inline">
                            {facility?.company}
                          </Typography>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  {facility?.address && (
                    <Grid item>
                      <Tooltip title="Facility address">
                        <Grid container direction="row" justifyContent="left" alignItems="top">
                          <Grid item xs={1} sx={{ mt: 0.5 }}>
                            <Typography variant="subtitle2" display="inline">
                              <Iconify
                                icon="mdi:house-outline"
                                width={16}
                                height={16}
                                color="secondary"
                                sx={{ mr: 0.5 }}
                              />
                            </Typography>
                          </Grid>
                          <Grid item xs={11}>
                            <Typography variant="subtitle2" display="inline">
                              <Link
                                target="_blank"
                                rel="noopener"
                                display="inline"
                                to={`https://www.google.com/maps/place/${facility?.address}`}
                                component={RouterLink}
                                sx={{ textDecoration: 'underline', '&:hover': { color: 'primary.light' } }}
                              >
                                {facility?.address}
                              </Link>
                            </Typography>
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  {facility?.phoneNumber && (
                    <Grid item>
                      <Tooltip title="Facility phone number">
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                          <Typography variant="subtitle2" display="inline">
                            <Iconify
                              icon="ic:baseline-local-phone"
                              width={16}
                              height={16}
                              color="secondary"
                              sx={{ mr: 0.5 }}
                            />
                          </Typography>
                          <Typography variant="subtitle2" display="inline">
                            {facility?.phoneNumber}
                          </Typography>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  {facility?.nhaEmail && (
                    <Grid item>
                      <Tooltip title="NHA Email">
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                          <Typography variant="subtitle2" display="inline">
                            <Iconify
                              icon="ic:outline-email"
                              width={16}
                              height={16}
                              color="secondary"
                              sx={{ mr: 0.5 }}
                            />
                          </Typography>
                          <Typography variant="subtitle2" display="inline">
                            {facility?.nhaEmail}
                          </Typography>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  {facility?.beds > -1 && (
                    <Grid item>
                      <Tooltip title="Facility beds">
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                          <Typography variant="subtitle2" display="inline">
                            <Iconify icon="mdi:bed-outline" width={16} height={16} color="secondary" sx={{ mr: 0.5 }} />
                          </Typography>
                          <Typography variant="subtitle2" display="inline">
                            {facility?.beds} beds
                          </Typography>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  {facility?.censusNumber > -1 && (
                    <Grid item>
                      <Tooltip title="Facility census">
                        <Grid container direction="row" justifyContent="center" alignItems="center">
                          <Typography variant="subtitle2" display="inline">
                            <Iconify
                              icon="mdi:person-outline"
                              width={16}
                              height={16}
                              color="secondary"
                              sx={{ mr: 0.5 }}
                            />
                          </Typography>
                          <Typography variant="subtitle2" display="inline">
                            Census {facility?.censusNumber}
                          </Typography>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  {facility?.facilityContacts && (
                    <Grid item>
                      <FacilityContacts facilityContacts={facility?.facilityContacts} />
                    </Grid>
                  )}
                  {facility?.facilityMapFile && (
                    <Grid item>
                      <Tooltip title="Facility map">
                        <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                          <Typography variant="subtitle2" display="inline">
                            <MapIcon width={12} height={12} color="secondary" sx={{ mr: 0.5, fontSize: '16px' }} />
                            <Link
                              onClick={() => downloadFacilityMap()}
                              sx={{ mb: 0.5, textDecoration: 'underline', '&:hover': { color: 'primary.light' } }}
                            >
                              {facility?.facilityMapFile}
                            </Link>
                          </Typography>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  )}
                  <Grid item sx={{ width: '100%' }}>
                    <WeatherInfo
                      loading={loadingWeather}
                      hasFetchedWeather={hasFetchedWeather}
                      zip={getZipCode(facility?.address)}
                      weatherInfo={weatherInfo}
                      stateAlerts={stateAlerts}
                      variant="subtitle2"
                    />
                  </Grid>
                  <Grid item>
                    {ticketManagers && (
                      <Typography variant="subtitle2" display="inline">
                        <Iconify sx={{ mr: 0.5 }} icon="hugeicons:manager" width={16} height={16} color="secondary" />
                        Managers:
                        {hasFacilityAccess && !!managerParams?.row && !!managerParams?.api ? (
                          <ManagerChips
                            params={managerParams}
                            type="Tickets"
                            hasFacilityAccess={hasFacilityAccess}
                            facilityManagers={ticketManagers}
                            refetch={() => dispatch(getActiveTickets(false))}
                            onDeleteSuccess={() =>
                              enqueueSnackbar('Manager deleted successfully', { variant: 'success' })
                            }
                          />
                        ) : (
                          ticketManagers?.map((manager) => (
                            <Chip
                              key={manager.facilityManagerId}
                              label={manager.manager}
                              color="primary"
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          ))
                        )}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ mb: 2, px: 3, py: 1 }}>
        <TicketSharedContent />
      </Card>

      <Card sx={{ height: '100%', mb: 2, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: '100%' }}>
          <Typography color="info.main" variant="h6">
            Recipients:
          </Typography>
        </Stack>
        <Typography variant="subtitle2">Main Ticket {id}:</Typography>
        <Grid container direction="column" justifyContent="center" alignItems="flex-start" spacing={4}>
          <Grid item sx={{ mt: 2 }}>
            <Box>
              <Grid container>
                {participants?.length > 0 && (
                  <>
                    {participants?.map((participant, index) => (
                      <Box key={participant.id}>
                        <Chip
                          key={participant.id}
                          label={participant.value || participant?.id}
                          onDelete={() => handleDelete(participants.filter((user) => user.id !== participant.id))}
                          avatar={
                            <CustomAvatar
                              name={participant.value || participant?.id}
                              email={participant.value && participant?.id}
                              index={index}
                              key={index}
                              sx={{ '&.MuiChip-avatar': { color: '#fff' } }}
                            />
                          }
                          sx={{ mr: 1, mb: 1, maxWidth: '180px' }}
                        />
                      </Box>
                    ))}
                  </>
                )}
                <Box sx={{ mt: 0.5 }}>
                  <NewUser
                    participants={participants}
                    setParticipants={setParticipants}
                    ticketId={id}
                    parentTicketId={id}
                  />
                </Box>
              </Grid>
            </Box>
          </Grid>
        </Grid>
        {subtickets?.map((sub) => (
          <Fragment key={sub.id}>
            {sub.users && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">Subticket {sub.ticketName}:</Typography>
                <TicketRecipients subticketId={sub.id} />
              </Box>
            )}
          </Fragment>
        ))}
      </Card>
    </>
  );
}
